import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import {
  aceptarOrdenCompraHttp,
  getOrdenCompraPorIdHttp,
  subirPDFFacturaHttp,
  validarFacturaOrdenCompraHttp,
} from "actions/ordenCompra";

export const useOrdenCompraProveedor = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const IdOrdenCompra = searchParams.get("id");
  const intl = useIntl();
  const navigate = useNavigate();

  const idUsuario = useSelector((state: StoreType) => state?.app?.user?.data?.id_usuario || 0);
  const user = useSelector((state: StoreType) => state?.app?.user?.data);

  const [procesando, setProcesando] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  const [ordenCompra, setOrdenCompra] = useState<any>(null);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //procesos orden compra
  const [procesandoOrdenCompra, setProcesandoOrdenCompra] = useState<boolean>(false);
  //Generación PDF
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
  const handleOpenPDFViewer = () => setIsPDFViewerOpen(true);
  const handleClosePDFViewer = () => setIsPDFViewerOpen(false);

  // Handlers para la paginación
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  //validacion xml y factura pdf
  const [procesandoValidandoFactura, setProcesandoValidandoFactura] = useState<boolean>(false);
  const [procesandoSubirPDFFactura, setProcesandoSubirPDFFactura] = useState<boolean>(false);
  const [facturaValidada, setFacturaValidada] = useState<boolean>(false);
  const [factura, setFactura] = useState<File | null>(null);
  const handleChangeFactura = (newFile: File | null) => {
    setFactura(newFile);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const detallesOrdenCompraPaginados = useMemo(() => {
    const detalles = ordenCompra?.detalles ?? [];
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return detalles.slice(startIndex, endIndex);
  }, [ordenCompra, page, rowsPerPage]);

  const getOrdenCompraPorId = useCallback(async (data: any) => {
    try {
      setProcesando(true);
      const response = await getOrdenCompraPorIdHttp(data);
      setOrdenCompra(response);
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
      handleisAlertOpen();
    }
  }, []);

  const aceptarOrdenCompra = async (datos: any) => {
    try {
      setProcesandoOrdenCompra(true);
      await aceptarOrdenCompraHttp(datos);
      if (IdOrdenCompra) {
        getOrdenCompraPorId({ id_orden_compra: IdOrdenCompra });
      }
      setMensajeAlert(intl.formatMessage({ id: "general_aceptada_correctamente" }));
      handleisAlertOpen();
      setProcesandoOrdenCompra(false);
    } catch (error) {
      setProcesandoOrdenCompra(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_aceptada_error" }));
      handleisAlertOpen();
    }
  };

  const subirPDFFactura = async (data: any) => {
    try {
      setProcesando(true);

      const formData = new FormData();
      formData.append("id_orden_compra", data.id_orden_compra);
      formData.append("id_requisicion", data.id_requisicion);
      formData.append("id_proveedor", data.id_proveedor);
      formData.append("detalles", JSON.stringify(data.detalles));
      formData.append("pdf_factura", data.pdf_factura);

      const response: any = await subirPDFFacturaHttp(formData);
      if (IdOrdenCompra) {
        getOrdenCompraPorId({ id_orden_compra: IdOrdenCompra });
      }
      setMensajeAlert(intl.formatMessage({ id: "pdf_factura_subido_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
      setFactura(null);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "pdf_factura_subido_error" }));
      handleisAlertOpen();
    }
  };

  const validarFacturaOrdenCompra = async (data: any) => {
    try {
      setProcesandoValidandoFactura(true);

      const formData = new FormData();
      formData.append("id_orden_compra", data.id_orden_compra);
      formData.append("id_proveedor", data.id_proveedor);
      formData.append("xml_factura", data.xml_factura);

      const response: any = await validarFacturaOrdenCompraHttp(formData);
      if (response.validacion.status === "success") {
        setFacturaValidada(true);
      }
      if (IdOrdenCompra) {
        getOrdenCompraPorId({ id_orden_compra: IdOrdenCompra });
      }
      setMensajeAlert(intl.formatMessage({ id: "factura_validada_correctamente" }));
      handleisAlertOpen();
      setProcesandoValidandoFactura(false);
      // Limpiar el archivo después de validar
      setFactura(null);
    } catch (error) {
      setProcesandoValidandoFactura(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "factura_validada_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (IdOrdenCompra) {
      getOrdenCompraPorId({ id_orden_compra: IdOrdenCompra });
    }
  }, []);

  return {
    intl,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    ordenCompra,
    page,
    rowsPerPage,
    idUsuario,
    handleChangePage,
    handleChangeRowsPerPage,
    detallesOrdenCompraPaginados,
    //procesos orden de compra
    procesandoOrdenCompra,
    aceptarOrdenCompra,
    //proceso xml y pdf factura
    factura,
    setFactura,
    handleChangeFactura,
    procesandoValidandoFactura,
    subirPDFFactura,
    validarFacturaOrdenCompra,
  };
};
